import { useQuery } from '@tanstack/react-query';
import { api } from '../../config';

export interface ApiKey {
  id: string;
  userId: string;
  name: string;
  keyPrefix: string;
  expiresAt?: string;
  lastUsedAt?: string;
  createdAt: string;
  isActive: boolean;
  rateLimit?: number;
  ipWhitelist?: string[];
  totalRequests: number;
}

export interface ApiKeyStats {
  totalRequests: number;
  dailyRequests: { date: string; count: number }[];
  hourlyRequests: { hour: number; count: number }[];
  endpointUsage: { endpoint: string; count: number }[];
  recentRequests: {
    timestamp: string;
    endpoint: string;
    statusCode: number;
    responseTime: number;
    ipAddress: string;
  }[];
}

export const useApiKeys = () => {
  return useQuery<{ apiKeys: ApiKey[] }, Error>({
    queryKey: ['apiKeys'],
    queryFn: async () => {
      return api.get<{ apiKeys: ApiKey[] }>('/api/v1/api-keys');
    },
  });
};

export const useApiKey = (keyId: string | null) => {
  return useQuery<{ apiKey: ApiKey }, Error>({
    queryKey: ['apiKey', keyId],
    queryFn: async () => {
      if (!keyId) throw new Error('No key ID provided');
      return api.get<{ apiKey: ApiKey }>(`/api/v1/api-keys/${keyId}`);
    },
    enabled: !!keyId,
  });
};

export const useApiKeyStats = (keyId: string | null) => {
  return useQuery<{ stats: ApiKeyStats }, Error>({
    queryKey: ['apiKeyStats', keyId],
    queryFn: async () => {
      if (!keyId) throw new Error('No key ID provided');
      return api.get<{ stats: ApiKeyStats }>(`/api/v1/api-keys/${keyId}/stats`);
    },
    enabled: !!keyId,
  });
};

export interface DeletedApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  userId: string;
  userEmail: string;
  deletedAt: string;
  deletedBy: string | null;
  deletedByEmail: string | null;
  deletionReason: string | null;
  daysUntilPermanentDeletion: number;
  createdAt: string;
}

export interface DeletedApiKeysResponse {
  keys: DeletedApiKey[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiKeyHistoryEntry {
  historyId: string;
  keyPrefix: string;
  regeneratedAt: string;
  regeneratedBy: string | null;
  originalCreatedAt: string;
}

export const useDeletedApiKeys = (options: {
  page?: number;
  pageSize?: number;
  search?: string;
} = {}) => {
  const { page = 1, pageSize = 20, search } = options;

  return useQuery<DeletedApiKeysResponse, Error>({
    queryKey: ['apiKeys', 'trash', { page, pageSize, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
      if (search) params.set('search', search);

      return api.get<DeletedApiKeysResponse>(`/api/v1/api-keys/trash?${params.toString()}`);
    },
  });
};

export const useApiKeyHistory = (keyId: string | null) => {
  return useQuery<{ history: ApiKeyHistoryEntry[] }, Error>({
    queryKey: ['apiKeyHistory', keyId],
    queryFn: async () => {
      if (!keyId) throw new Error('No key ID provided');
      return api.get<{ history: ApiKeyHistoryEntry[] }>(`/api/v1/api-keys/${keyId}/history`);
    },
    enabled: !!keyId,
  });
};